import React, { forwardRef } from 'react'
import { useTranslation } from 'react-i18next'
import QtyNumberInput from '../../../../../../order/components/CellTemplates/components/QtyNumberInput'
import { CentricTypography as Typography } from '@core/component/CentricTypography'
import { makeStyles, Tooltip } from '@material-ui/core'
import classNames from 'classnames'
import { RollUpImg, SplitProductQtyImg } from '@resources/images'
import Icon from '@components/std-components/Icons'
import { DoorToSizeQuantityDistributionToggle } from './EnhancedQuantityCell'
import EnhancedQtyCheckbox from './EnhancedQtyCheckbox'

export interface BookAndActualQtyProps {
  value: number
  handleInputChange: any
  isBooked: boolean
  onChange: (e: any) => void
  disabled: boolean
  isBookOpPending: boolean
  isHardExcluded: boolean
  onKeyDown: any
  autoFocus: boolean
  onFocus: any
  onMouseDown: any
  label?: string
  showCheckbox?: boolean
  onBlur?: () => void
  highlightDoors?: boolean
  highlightQty?: boolean
  doorToSizeQuantityDistributionToggle?: DoorToSizeQuantityDistributionToggle
  handleSplitOrRollupDoorsQty?: (isSplitOperation: boolean) => void
}

const useStyles = makeStyles(theme => ({
  checkboxContainer: {
    padding: '10px 0',
    height: 53,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%'
  },
  doorQuantity: {
    padding: 10,
    width: '100%',
    background: theme.palette.background.default,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderBottom: '1px solid #EEEEEE',
    borderTop: '1px solid #EEEEEE'
  },
  doorHighlightQuantity: {
    padding: 10,
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderBottom: '1px solid #EEEEEE',
    borderTop: '1px solid #EEEEEE'
  },
  doorContainer: {
    display: 'flex',
    alignItems: 'center',
    height: 35,
    flexDirection: 'column',
    width: 129
  },
  input: {
    width: '80px',
    height: '20px',
    marginTop: '0px'
  },
  iconBtn: {
    cursor: 'pointer',
    '& .icon': {
      cursor: 'inherit'
    }
  },
  disabledBtn: {
    opacity: 0.5,
    cursor: 'not-allowed'
  },
  inputWrapper: {
    display: 'flex',
    width: '100%',
    justifyContent: 'space-evenly',
    alignItems: 'center',
    marginTop: 4
  },
  inputWrapperHighlightQty: {
    marginTop: 6,
    gap: 2
  },
  icon: { height: 20, width: 20 },
  splitIconStyle: {
    visibility: 'hidden'
  }
}))

/**
 * Sub-component at top of List-View and Vertical-Detail door cell with book/unbook checkbox and door quantity input
 */
const EnhancedBookAndActualQty = forwardRef<HTMLInputElement, BookAndActualQtyProps>(
  (
    {
      value,
      handleInputChange,
      isBooked,
      onChange,
      disabled,
      isBookOpPending,
      isHardExcluded,
      onKeyDown,
      autoFocus,
      onFocus,
      onMouseDown,
      label,
      showCheckbox = true,
      onBlur,
      highlightDoors,
      highlightQty,
      doorToSizeQuantityDistributionToggle,
      handleSplitOrRollupDoorsQty
    },
    ref
  ) => {
    const { t } = useTranslation()

    const classes = useStyles()
    const {
      displaySplitOrRollUpIcon,
      disabled: disableQuantitySplit,
      hideSplitDownIcon
    } = doorToSizeQuantityDistributionToggle
    return (
      <div>
        <div className={classes.checkboxContainer}>
          {showCheckbox && (
            <EnhancedQtyCheckbox
              onChange={onChange}
              isBooked={isBooked}
              isBookOpPending={isBookOpPending}
              isHardExcluded={isHardExcluded}
              disabled={disabled}
            />
          )}
        </div>
        <div
          className={
            highlightDoors ? classes.doorHighlightQuantity : classes.doorQuantity
          }
        >
          <div className={classes.doorContainer}>
            <Typography variant='body2'>
              {label ?? t('orderTable.cnfActualQty', 'Door Quantity')}
            </Typography>

            <div
              className={classNames(classes.inputWrapper, {
                [classes.inputWrapperHighlightQty]: highlightQty
              })}
            >
              {displaySplitOrRollUpIcon ? (
                <div
                  onClick={() => {
                    if (!isBooked || disableQuantitySplit || disabled) {
                      return
                    }
                    handleSplitOrRollupDoorsQty(false)
                  }}
                  className={classNames(classes.iconBtn, {
                    [classes.disabledBtn]: !isBooked || disableQuantitySplit || disabled
                  })}
                >
                  <Tooltip title={t('order.rollUp', 'Roll Up')}>
                    <div>
                      <Icon imageSrc={RollUpImg} alt={'rollUp'} />
                    </div>
                  </Tooltip>
                </div>
              ) : null}
              <QtyNumberInput
                onChange={handleInputChange}
                disabled={!isBooked || disabled}
                isReset={!isBooked}
                value={value}
                onKeyDown={onKeyDown}
                ref={ref}
                autoFocus={autoFocus}
                onFocus={onFocus}
                onMouseDown={onMouseDown}
                onBlur={onBlur}
                className={classes.input}
                highlightInput={highlightQty}
              />

              {displaySplitOrRollUpIcon ? (
                <div
                  onClick={() => {
                    if (!isBooked || disableQuantitySplit || disabled) {
                      return
                    }
                    handleSplitOrRollupDoorsQty(true)
                  }}
                  className={classNames(classes.iconBtn, {
                    [classes.disabledBtn]: !isBooked || disableQuantitySplit || disabled,
                    [classes.splitIconStyle]: hideSplitDownIcon
                  })}
                >
                  <Tooltip title={t('order.splitDoorQty', 'Split Door QTY')}>
                    <div>
                      <Icon imageSrc={SplitProductQtyImg} alt={'splitDoorQty'} />
                    </div>
                  </Tooltip>
                </div>
              ) : null}
            </div>
          </div>
        </div>
      </div>
    )
  }
)

export default EnhancedBookAndActualQty
